<template>
  <div class="mx-auto p-4 bg-warm-gray-900 min-h-screen pb-20">
    <div class="container mx-auto">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <button
            @click="$router.push('/endorsers')"
            class="mb-2 text-sm text-gray-400 hover:text-gray-200 transition-colors flex items-center gap-1"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Endorsers
          </button>
          <h1 class="text-2xl font-bold text-gray-100">Endorser Details</h1>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="text-gray-400">Loading endorser data...</div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="p-4 bg-red-900 border border-red-700 rounded mb-6">
        <p class="text-red-200">{{ error }}</p>
      </div>

      <!-- Endorser Data -->
      <div v-else-if="endorser" class="space-y-6">
        <!-- Endorser Information Card -->
        <div class="bg-warm-gray-800 rounded-lg p-6 border border-warm-gray-700">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-bold text-gray-100">Endorser Information</h2>
            <button
              @click="toggleEditMode"
              :disabled="updatingEndorser"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="isEditingEndorser">Cancel</span>
              <span v-else>Edit</span>
            </button>
          </div>
          
          <!-- View Mode -->
          <div v-if="!isEditingEndorser" class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="text-sm text-gray-400">First Name</label>
              <p class="text-gray-100 font-medium">{{ endorser.data.firstName || 'N/A' }}</p>
            </div>
            <div>
              <label class="text-sm text-gray-400">Last Name</label>
              <p class="text-gray-100 font-medium">{{ endorser.data.lastName || 'N/A' }}</p>
            </div>
            <div>
              <label class="text-sm text-gray-400">Email</label>
              <p class="text-gray-100">{{ endorser.data.email || 'N/A' }}</p>
            </div>
            <div>
              <label class="text-sm text-gray-400">Personal Email</label>
              <p class="text-gray-100">{{ endorser.data.personalEmail || 'N/A' }}</p>
            </div>
            <div>
              <label class="text-sm text-gray-400">Commission Amount</label>
              <p class="text-gray-100 font-medium">₱{{ formatCurrency(endorser.data.commissionAmount || 0) }}</p>
            </div>
            <div>
              <label class="text-sm text-gray-400">Discount Amount</label>
              <p class="text-gray-100 font-medium">₱{{ formatCurrency(endorser.data.discountAmount || 0) }}</p>
            </div>
            <div>
              <label class="text-sm text-gray-400">Max Coupons</label>
              <p class="text-gray-100 font-medium">{{ endorser.data.maxCoupons || 0 }}</p>
            </div>
            <div>
              <label class="text-sm text-gray-400">Payout Threshold</label>
              <p class="text-gray-100 font-medium">₱{{ formatCurrency(endorser.data.payoutThreshold || 0) }}</p>
            </div>
            <div>
              <label class="text-sm text-gray-400">Running Commission</label>
              <p class="font-medium" :class="runningCommission > 0 ? 'text-green-400' : 'text-gray-100'">
                ₱{{ formatCurrency(runningCommission) }}
              </p>
            </div>
            <div>
              <label class="text-sm text-gray-400">Bank Name</label>
              <p class="text-gray-100">{{ endorser.data.bankName || 'N/A' }}</p>
            </div>
            <div>
              <label class="text-sm text-gray-400">Bank Account Number</label>
              <p class="text-gray-100">{{ endorser.data.bankAccountNumber || 'N/A' }}</p>
            </div>
          </div>
          
          <!-- Edit Mode -->
          <form v-else @submit.prevent="handleUpdateEndorser" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">
                  First Name <span class="text-red-400">*</span>
                </label>
                <input
                  v-model="endorserFormData.firstName"
                  type="text"
                  required
                  :disabled="updatingEndorser"
                  class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">
                  Last Name <span class="text-red-400">*</span>
                </label>
                <input
                  v-model="endorserFormData.lastName"
                  type="text"
                  required
                  :disabled="updatingEndorser"
                  class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                <input
                  v-model="endorserFormData.email"
                  type="email"
                  disabled
                  class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-500 cursor-not-allowed"
                />
                <p class="text-xs text-gray-400 mt-1">Email cannot be changed</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">
                  Personal Email <span class="text-red-400">*</span>
                </label>
                <input
                  v-model="endorserFormData.personalEmail"
                  type="email"
                  required
                  :disabled="updatingEndorser"
                  class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">
                  Commission Amount <span class="text-red-400">*</span>
                </label>
                <div class="relative">
                  <span class="absolute left-3 top-2 text-gray-400">₱</span>
                  <input
                    v-model.number="endorserFormData.commissionAmount"
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    :disabled="updatingEndorser"
                    class="w-full pl-8 pr-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">
                  Discount Amount <span class="text-red-400">*</span>
                </label>
                <div class="relative">
                  <span class="absolute left-3 top-2 text-gray-400">₱</span>
                  <input
                    v-model.number="endorserFormData.discountAmount"
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    :disabled="updatingEndorser"
                    class="w-full pl-8 pr-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">
                  Max Coupons <span class="text-red-400">*</span>
                </label>
                <input
                  v-model.number="endorserFormData.maxCoupons"
                  type="number"
                  min="0"
                  required
                  :disabled="updatingEndorser"
                  class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">
                  Payout Threshold <span class="text-red-400">*</span>
                </label>
                <div class="relative">
                  <span class="absolute left-3 top-2 text-gray-400">₱</span>
                  <input
                    v-model.number="endorserFormData.payoutThreshold"
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    :disabled="updatingEndorser"
                    class="w-full pl-8 pr-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="0.00"
                  />
                </div>
                <p class="text-xs text-gray-400 mt-1">Minimum commission amount required before endorser can request a payout</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">
                  Bank Name
                </label>
                <input
                  v-model="endorserFormData.bankName"
                  type="text"
                  :disabled="updatingEndorser"
                  class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="e.g., GCash, BPI, BDO, PayMaya"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">
                  Bank Account Number
                </label>
                <input
                  v-model="endorserFormData.bankAccountNumber"
                  type="text"
                  :disabled="updatingEndorser"
                  class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Account number or mobile number"
                />
              </div>
            </div>
            
            <div class="flex gap-3 pt-4">
              <button
                type="button"
                @click="toggleEditMode"
                :disabled="updatingEndorser"
                class="flex-1 px-4 py-2 bg-warm-gray-700 hover:bg-warm-gray-600 text-gray-300 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="updatingEndorser"
                class="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="updatingEndorser">Saving...</span>
                <span v-else>Save Changes</span>
              </button>
            </div>
          </form>
        </div>

        <!-- Unused Coupons Section -->
        <div class="bg-warm-gray-800 rounded-lg p-6 border border-warm-gray-700">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-bold text-gray-100">
              Unused Coupons
              <span v-if="!couponsLoading && !couponsError && endorser" class="text-base font-normal text-gray-400 ml-2">
                ({{ unusedCoupons.length }}/{{ endorser.data.maxCoupons || 0 }})
              </span>
            </h2>
            <button
              @click="createCoupon"
              :disabled="creatingCoupon || couponsLoading || !endorser"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="creatingCoupon" class="flex items-center gap-2">
                <svg
                  class="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Creating...
              </span>
              <span v-else>Create Coupon</span>
            </button>
          </div>
          
          <!-- Coupons Loading State -->
          <div v-if="couponsLoading" class="text-center py-8">
            <div class="text-gray-400">Loading coupons...</div>
          </div>

          <!-- Coupons Error State -->
          <div v-else-if="couponsError" class="p-4 bg-red-900 border border-red-700 rounded mb-4">
            <p class="text-red-200">{{ couponsError }}</p>
          </div>

          <!-- Unused Coupons Table -->
          <div v-else-if="unusedCoupons.length > 0" class="overflow-x-auto">
            <table class="w-full text-sm text-left">
              <thead class="text-xs text-gray-400 uppercase border-b border-warm-gray-700">
                <tr>
                  <th class="px-4 py-3">Code</th>
                  <th class="px-4 py-3">Status</th>
                  <th class="px-4 py-3">Discount</th>
                  <th class="px-4 py-3">Commission</th>
                  <th class="px-4 py-3">Created</th>
                  <th class="px-4 py-3">Expiry</th>
                  <th class="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody class="text-gray-300">
                <tr
                  v-for="coupon in unusedCoupons"
                  :key="coupon.id"
                  class="border-b border-warm-gray-800 transition-all hover:bg-gray-800/50"
                >
                  <td class="px-4 py-3">
                    <div class="font-mono font-medium text-gray-100">{{ coupon.data.code }}</div>
                  </td>
                  <td class="px-4 py-3">
                    <span
                      class="px-2 py-1 text-xs font-medium rounded"
                      :class="getStatusClass(getUIStatus(coupon))"
                    >
                      {{ getUIStatus(coupon) }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-gray-100">
                    ₱{{ formatCurrency(coupon.data.discountAmount || 0) }}
                  </td>
                  <td class="px-4 py-3 text-gray-100">
                    ₱{{ formatCurrency(coupon.data.commissionAmount || 0) }}
                  </td>
                  <td class="px-4 py-3 text-gray-400">
                    {{ formatDate(coupon.data.createdAt) }}
                  </td>
                  <td class="px-4 py-3 text-gray-400">
                    {{ formatDate(coupon.data.expiryDate) }}
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-2">
                      <span v-if="getUIStatus(coupon) === 'expired'" class="text-gray-500 text-xs">—</span>
                      
                      <!-- Deactivate button - show for active or assigned coupons (not expired) -->
                      <button
                        v-else-if="coupon.data.status === 'active' || coupon.data.status === 'assigned'"
                        @click="deactivateCoupon(coupon.id, coupon.data.code)"
                        :disabled="deactivatingCouponId === coupon.id || activatingCouponId === coupon.id"
                        class="px-2 py-1 text-xs font-medium text-white bg-gray-600 hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Deactivate this coupon"
                      >
                        <span v-if="deactivatingCouponId === coupon.id" class="flex items-center gap-1">
                          <svg
                            class="animate-spin h-3 w-3"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            ></circle>
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Deactivating...
                        </span>
                        <span v-else>Deactivate</span>
                      </button>
                      
                      <!-- Activate and Delete buttons - show for deactivated coupons (not expired) -->
                      <template v-else-if="coupon.data.status === 'deactivated'">
                        <button
                          @click="activateCoupon(coupon.id, coupon.data.code)"
                          :disabled="activatingCouponId === coupon.id"
                          class="px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Activate this coupon"
                        >
                          <span v-if="activatingCouponId === coupon.id" class="flex items-center gap-1">
                            <svg
                              class="animate-spin h-3 w-3"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                              ></circle>
                              <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Activating...
                          </span>
                          <span v-else>Activate</span>
                        </button>
                        
                      </template>
                      
                      <span v-else class="text-gray-500 text-xs">—</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty Unused Coupons State -->
          <div v-else class="text-center py-8 text-gray-400">
            <p>No unused coupons found for this endorser.</p>
          </div>
        </div>

        <!-- Used Coupons Section -->
        <div class="bg-warm-gray-800 rounded-lg p-6 border border-warm-gray-700">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h2 class="text-xl font-bold text-gray-100">
              Used Coupons
              <span v-if="!usedCouponsLoading && !couponsError" class="text-base font-normal text-gray-400 ml-2">
                ({{ usedCoupons.length }})
              </span>
            </h2>
            
            <!-- Month Browser -->
            <div v-if="!usedCouponsLoading && !couponsError" class="flex items-center justify-center gap-2">
              <button
                @click="previousMonth"
                class="p-1 text-gray-400 hover:text-gray-200 hover:bg-warm-gray-700 rounded transition-colors"
                title="Previous month"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div class="text-sm font-medium text-gray-200 text-center px-2 min-w-[120px]">
                {{ formatMonthYear(selectedMonth, selectedYear) }}
              </div>
              <button
                @click="nextMonth"
                class="p-1 text-gray-400 hover:text-gray-200 hover:bg-warm-gray-700 rounded transition-colors"
                title="Next month"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            <!-- Total Commission Summary -->
            <div v-if="!usedCouponsLoading && !couponsError" class="text-center sm:text-right">
              <div class="text-sm text-gray-400">Total Commission:</div>
              <div class="text-base font-semibold text-gray-100">₱{{ formatCurrency(totalUsedCommission) }}</div>
            </div>
          </div>
          
          <!-- Coupons Loading State -->
          <div v-if="couponsLoading" class="text-center py-8">
            <div class="text-gray-400">Loading coupons...</div>
          </div>

          <!-- Coupons Error State -->
          <div v-else-if="couponsError" class="p-4 bg-red-900 border border-red-700 rounded mb-4">
            <p class="text-red-200">{{ couponsError }}</p>
          </div>

          <!-- Used Coupons Table -->
          <div v-else-if="usedCouponsByMonth.length > 0" class="overflow-x-auto">
            <table class="w-full text-sm text-left">
              <thead class="text-xs text-gray-400 uppercase border-b border-warm-gray-700">
                <tr>
                  <th class="px-4 py-3">Code</th>
                  <th class="px-4 py-3">Status</th>
                  <th class="px-4 py-3">Discount</th>
                  <th class="px-4 py-3">Commission</th>
                  <th class="px-4 py-3">Created</th>
                  <th class="px-4 py-3">Expiry</th>
                  <th class="px-4 py-3">Used At</th>
                  <th class="px-4 py-3">Used By Email</th>
                  <th class="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody class="text-gray-300">
                <tr
                  v-for="coupon in usedCouponsByMonth"
                  :key="coupon.id"
                  class="border-b border-warm-gray-800 transition-all hover:bg-gray-800/50"
                >
                  <td class="px-4 py-3">
                    <div class="font-mono font-medium text-gray-100">{{ coupon.data.code }}</div>
                  </td>
                  <td class="px-4 py-3">
                    <span
                      class="px-2 py-1 text-xs font-medium rounded"
                      :class="getStatusClass(getUIStatus(coupon))"
                    >
                      {{ getUIStatus(coupon) }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-gray-100">
                    ₱{{ formatCurrency(coupon.data.discountAmount || 0) }}
                  </td>
                  <td class="px-4 py-3 text-gray-100">
                    ₱{{ formatCurrency(coupon.data.commissionAmount || 0) }}
                  </td>
                  <td class="px-4 py-3 text-gray-400">
                    {{ formatDate(coupon.data.createdAt) }}
                  </td>
                  <td class="px-4 py-3 text-gray-400">
                    {{ formatDate(coupon.data.expiryDate) }}
                  </td>
                  <td class="px-4 py-3 text-gray-400">
                    {{ formatDate(coupon.data.usedAt) }}
                  </td>
                  <td class="px-4 py-3 text-gray-400">
                    <span v-if="coupon.data.usedByEmail">{{ coupon.data.usedByEmail }}</span>
                    <span v-else class="text-gray-500">—</span>
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-2">
                      <button
                        v-if="coupon.data.status !== 'revoked'"
                        @click="revokeCoupon(coupon.id, coupon.data.code)"
                        :disabled="revokingCouponId === coupon.id"
                        class="px-2 py-1 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Revoke this coupon"
                      >
                        <span v-if="revokingCouponId === coupon.id" class="flex items-center gap-1">
                          <svg
                            class="animate-spin h-3 w-3"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            ></circle>
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Revoking...
                        </span>
                        <span v-else>Revoke</span>
                      </button>
                      <span v-else class="text-gray-500 text-xs">Revoked</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty Used Coupons State -->
          <div v-else-if="usedCoupons.length > 0" class="text-center py-8 text-gray-400">
            <p>No used coupons found for {{ formatMonthYear(selectedMonth, selectedYear) }}</p>
            <p class="text-sm mt-1">Try selecting a different month.</p>
          </div>
          <div v-else class="text-center py-8 text-gray-400">
            <p>No used coupons found for this endorser.</p>
          </div>
        </div>

        <!-- Payouts Section -->
        <div class="bg-warm-gray-800 rounded-lg p-6 border border-warm-gray-700">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-bold text-gray-100">
              Payouts
              <span v-if="!payoutsLoading && !payoutsError" class="text-base font-normal text-gray-400 ml-2">
                ({{ payouts.length }})
              </span>
            </h2>
            <button
              @click="openCreatePayoutModal"
              :disabled="creatingPayout || payoutsLoading || !endorser"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="creatingPayout" class="flex items-center gap-2">
                <svg
                  class="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Creating...
              </span>
              <span v-else>Create Payout</span>
            </button>
          </div>
          
          <!-- Payouts Loading State -->
          <div v-if="payoutsLoading" class="text-center py-8">
            <div class="text-gray-400">Loading payouts...</div>
          </div>

          <!-- Payouts Error State -->
          <div v-else-if="payoutsError" class="p-4 bg-red-900 border border-red-700 rounded mb-4">
            <p class="text-red-200">{{ payoutsError }}</p>
          </div>

          <!-- Payouts Table -->
          <div v-else-if="payouts.length > 0" class="overflow-x-auto">
            <table class="w-full text-sm text-left">
              <thead class="text-xs text-gray-400 uppercase border-b border-warm-gray-700">
                <tr>
                  <th class="px-4 py-3">REQUESTED</th>
                  <th class="px-4 py-3">STATUS</th>
                  <th class="px-4 py-3">TRANSACTION REFERENCE CODE</th>
                  <th class="px-4 py-3">TOTAL AMOUNT</th>
                  <th class="px-4 py-3">ACTIONS</th>
                </tr>
              </thead>
              <tbody class="text-gray-300">
                <tr
                  v-for="payout in payouts"
                  :key="payout.id"
                  class="border-b border-warm-gray-800 transition-all hover:bg-gray-800/50"
                >
                  <td class="px-4 py-3 text-gray-400">
                    {{ formatDate(payout.data.requestedAt) }}
                  </td>
                  <td class="px-4 py-3">
                    <span
                      class="px-2 py-1 text-xs font-medium rounded"
                      :class="getPayoutStatusClass(payout.data.status)"
                    >
                      {{ getPayoutStatusLabel(payout.data.status) }}
                    </span>
                  </td>
                  <td class="px-4 py-3">
                    <span v-if="payout.data.senderReferenceCode" class="font-mono text-gray-100">
                      {{ payout.data.senderReferenceCode }}
                    </span>
                    <span v-else class="text-gray-500">—</span>
                  </td>
                  <td class="px-4 py-3 text-gray-100 font-medium">
                    ₱{{ formatCurrency(payout.data.totalAmount || 0) }}
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-2">
                      <button
                        @click="viewPayout(payout)"
                        class="px-3 py-1 text-xs font-medium text-blue-400 hover:text-blue-300 underline transition-colors"
                      >
                        View
                      </button>
                      <button
                        v-if="payout.data.status === 'requested'"
                        @click="revokePayout(payout.id)"
                        :disabled="updatingPayoutId === payout.id"
                        class="px-3 py-1 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span v-if="updatingPayoutId === payout.id" class="flex items-center gap-1">
                          <svg
                            class="animate-spin h-3 w-3"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            ></circle>
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </span>
                        <span v-else>Revoke</span>
                      </button>
                      <button
                        v-if="(payout.data.status === 'requested' || payout.data.status === 'approved') && payout.data.status !== 'revoked'"
                        @click="openMarkAsPaidModal(payout)"
                        :disabled="updatingPayoutId === payout.id"
                        class="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span v-if="updatingPayoutId === payout.id" class="flex items-center gap-1">
                          <svg
                            class="animate-spin h-3 w-3"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            ></circle>
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </span>
                        <span v-else>Mark as Paid</span>
                      </button>
                      <button
                        v-if="payout.data.status === 'paid'"
                        @click="markPayoutAsComplete(payout.id)"
                        :disabled="updatingPayoutId === payout.id"
                        class="px-3 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span v-if="updatingPayoutId === payout.id" class="flex items-center gap-1">
                          <svg
                            class="animate-spin h-3 w-3"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            ></circle>
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </span>
                        <span v-else>Mark as Complete</span>
                      </button>
                      <button
                        v-if="payout.data.status === 'revoked'"
                        @click="deletePayout(payout.id)"
                        :disabled="updatingPayoutId === payout.id"
                        class="px-3 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span v-if="updatingPayoutId === payout.id" class="flex items-center gap-1">
                          <svg
                            class="animate-spin h-3 w-3"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            ></circle>
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Deleting...
                        </span>
                        <span v-else>Delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty Payouts State -->
          <div v-else class="text-center py-8 text-gray-400">
            <p>No payouts found for this endorser.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- View Payout Modal -->
    <div
      v-if="selectedPayout"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="closeViewPayoutModal"
    >
      <div class="bg-warm-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-warm-gray-700">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-gray-100">Payout Details</h3>
          <button
            @click="closeViewPayoutModal"
            class="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="text-sm text-gray-400">Status</label>
              <div class="mt-1">
                <span
                  class="px-2 py-1 text-xs font-medium rounded"
                  :class="getPayoutStatusClass(selectedPayout.data.status)"
                >
                  {{ getPayoutStatusLabel(selectedPayout.data.status) }}
                </span>
              </div>
            </div>
            <div>
              <label class="text-sm text-gray-400">Total Amount</label>
              <p class="text-gray-100 font-medium mt-1">₱{{ formatCurrency(selectedPayout.data.totalAmount || 0) }}</p>
            </div>
            <div>
              <label class="text-sm text-gray-400">Commission Amount</label>
              <p class="text-gray-100 mt-1">₱{{ formatCurrency(selectedPayout.data.commissionAmount || 0) }}</p>
            </div>
            <div>
              <label class="text-sm text-gray-400">Bonuses</label>
              <p class="text-gray-100 mt-1">
                <span v-if="selectedPayout.data.bonuses && selectedPayout.data.bonuses.length > 0">
                  ₱{{ formatCurrency(selectedPayout.data.bonuses.reduce((sum, b) => sum + (b.amount || 0), 0)) }}
                </span>
                <span v-else class="text-gray-500">None</span>
              </p>
            </div>
            <div>
              <label class="text-sm text-gray-400">Requested At</label>
              <p class="text-gray-100 mt-1">{{ formatDate(selectedPayout.data.requestedAt) }}</p>
            </div>
            <div v-if="selectedPayout.data.approvedAt">
              <label class="text-sm text-gray-400">Approved At</label>
              <p class="text-gray-100 mt-1">{{ formatDate(selectedPayout.data.approvedAt) }}</p>
            </div>
            <div v-if="selectedPayout.data.approvedByEmail">
              <label class="text-sm text-gray-400">Approved By</label>
              <p class="text-gray-100 mt-1">{{ selectedPayout.data.approvedByEmail }}</p>
            </div>
            <div v-if="selectedPayout.data.paidAt">
              <label class="text-sm text-gray-400">Paid At</label>
              <p class="text-gray-100 mt-1">{{ formatDate(selectedPayout.data.paidAt) }}</p>
            </div>
            <div v-if="selectedPayout.data.completedAt">
              <label class="text-sm text-gray-400">Completed At</label>
              <p class="text-gray-100 mt-1">{{ formatDate(selectedPayout.data.completedAt) }}</p>
            </div>
          </div>

          <div v-if="selectedPayout.data.recipientBankName || selectedPayout.data.recipientBankAccountNumber">
            <label class="text-sm text-gray-400 block mb-2">Recipient Bank Information</label>
            <div class="bg-warm-gray-900 rounded p-3 space-y-2">
              <div>
                <span class="text-xs text-gray-500">Bank Name:</span>
                <p class="text-gray-100">{{ selectedPayout.data.recipientBankName || 'N/A' }}</p>
              </div>
              <div>
                <span class="text-xs text-gray-500">Account Number:</span>
                <p class="text-gray-100">{{ selectedPayout.data.recipientBankAccountNumber || 'N/A' }}</p>
              </div>
            </div>
          </div>

          <div v-if="selectedPayout.data.senderBankName || selectedPayout.data.senderReferenceCode">
            <label class="text-sm text-gray-400 block mb-2">Sender Bank Information</label>
            <div class="bg-warm-gray-900 rounded p-3 space-y-2">
              <div>
                <span class="text-xs text-gray-500">Bank Name:</span>
                <p class="text-gray-100">{{ selectedPayout.data.senderBankName || 'N/A' }}</p>
              </div>
              <div>
                <span class="text-xs text-gray-500">Reference Code:</span>
                <p class="text-gray-100 font-mono">{{ selectedPayout.data.senderReferenceCode || 'N/A' }}</p>
              </div>
            </div>
          </div>

          <div v-if="selectedPayout.data.bonuses && selectedPayout.data.bonuses.length > 0">
            <label class="text-sm text-gray-400 block mb-2">Bonuses</label>
            <div class="bg-warm-gray-900 rounded p-3 space-y-2">
              <div v-for="(bonus, index) in selectedPayout.data.bonuses" :key="index" class="border-b border-warm-gray-700 pb-2 last:border-0 last:pb-0">
                <p class="text-gray-100 font-medium">₱{{ formatCurrency(bonus.amount || 0) }}</p>
                <p class="text-sm text-gray-400">{{ bonus.description || 'No description' }}</p>
              </div>
            </div>
          </div>

          <div v-if="selectedPayout.data.includedCouponIds && selectedPayout.data.includedCouponIds.length > 0">
            <label class="text-sm text-gray-400 block mb-2">Included Coupons</label>
            <p class="text-gray-100 text-sm">{{ selectedPayout.data.includedCouponIds.length }} coupon(s) included in this payout</p>
          </div>

          <div v-if="selectedPayout.data.notes">
            <label class="text-sm text-gray-400 block mb-2">Notes</label>
            <p class="text-gray-100 text-sm bg-warm-gray-900 rounded p-3">{{ selectedPayout.data.notes }}</p>
          </div>
        </div>

        <div class="mt-6 flex justify-end gap-3">
          <button
            @click="closeViewPayoutModal"
            class="px-4 py-2 bg-warm-gray-700 hover:bg-warm-gray-600 text-gray-300 rounded transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Mark as Paid Modal -->
    <div
      v-if="payoutToMarkAsPaid"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="closeMarkAsPaidModal"
    >
      <div class="bg-warm-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-warm-gray-700">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-gray-100">Mark Payout as Paid</h3>
          <button
            @click="closeMarkAsPaidModal"
            class="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form @submit.prevent="handleMarkAsPaid" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">
              Sender Bank Name <span class="text-red-400">*</span>
            </label>
            <input
              v-model="markAsPaidForm.senderBankName"
              type="text"
              required
              :disabled="updatingPayoutId === payoutToMarkAsPaid.id"
              class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="e.g., GCash, BPI, BDO, PayMaya"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">
              Reference Code <span class="text-red-400">*</span>
            </label>
            <input
              v-model="markAsPaidForm.senderReferenceCode"
              type="text"
              required
              :disabled="updatingPayoutId === payoutToMarkAsPaid.id"
              class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Transaction reference code"
            />
          </div>

          <div class="bg-warm-gray-900 rounded p-3">
            <p class="text-xs text-gray-400 mb-1">Total Amount</p>
            <p class="text-lg font-bold text-gray-100">₱{{ formatCurrency(payoutToMarkAsPaid.data.totalAmount || 0) }}</p>
          </div>

          <div class="flex gap-3 pt-4">
            <button
              type="button"
              @click="closeMarkAsPaidModal"
              :disabled="updatingPayoutId === payoutToMarkAsPaid.id"
              class="flex-1 px-4 py-2 bg-warm-gray-700 hover:bg-warm-gray-600 text-gray-300 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="updatingPayoutId === payoutToMarkAsPaid.id"
              class="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="updatingPayoutId === payoutToMarkAsPaid.id">Processing...</span>
              <span v-else>Mark as Paid</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Create Payout Modal -->
    <div
      v-if="showCreatePayoutModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="closeCreatePayoutModal"
    >
      <div class="bg-warm-gray-800 rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto border border-warm-gray-700">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-gray-100">Create Payout</h3>
          <button
            @click="closeCreatePayoutModal"
            class="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <!-- Loading state while calculating commission -->
        <div v-if="calculatingUnpaidCommission" class="text-center py-8">
          <div class="text-gray-400">Calculating unpaid commission...</div>
        </div>

        <!-- Error state -->
        <div v-else-if="createPayoutError" class="p-4 bg-red-900 border border-red-700 rounded mb-4">
          <p class="text-red-200">{{ createPayoutError }}</p>
        </div>

        <!-- Form -->
        <form v-else @submit.prevent="handleCreatePayout" class="space-y-4">
          <!-- Commission Amount Display -->
          <div class="bg-warm-gray-900 rounded p-4">
            <div class="flex justify-between items-center mb-2">
              <p class="text-sm text-gray-400">Unpaid Commission</p>
              <p class="text-xl font-bold text-gray-100">₱{{ formatCurrency(calculatedCommission || 0) }}</p>
            </div>
            <p class="text-xs text-gray-500 mt-1">{{ unpaidCouponCount }} coupon(s) will be included</p>
          </div>

          <!-- Sender Bank Name -->
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">
              Sender Bank Name <span class="text-red-400">*</span>
            </label>
            <input
              v-model="createPayoutForm.senderBankName"
              type="text"
              required
              :disabled="creatingPayout"
              class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="e.g., GCash, BPI, BDO, PayMaya"
            />
          </div>
          
          <!-- Reference Code -->
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">
              Reference Code <span class="text-red-400">*</span>
            </label>
            <input
              v-model="createPayoutForm.senderReferenceCode"
              type="text"
              required
              :disabled="creatingPayout"
              class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Transaction reference code"
            />
          </div>

          <!-- Notes -->
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">
              Notes
            </label>
            <textarea
              v-model="createPayoutForm.notes"
              :disabled="creatingPayout"
              rows="3"
              class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Optional notes about this payout"
            ></textarea>
          </div>

          <!-- Recipient Bank Info (read-only, from endorser) -->
          <div v-if="endorser && (endorser.data.bankName || endorser.data.bankAccountNumber)" class="bg-warm-gray-900 rounded p-3">
            <p class="text-xs text-gray-400 mb-2">Recipient Bank Information</p>
            <div class="space-y-1">
              <div>
                <span class="text-xs text-gray-500">Bank Name:</span>
                <p class="text-gray-100">{{ endorser.data.bankName || 'N/A' }}</p>
              </div>
              <div>
                <span class="text-xs text-gray-500">Account Number:</span>
                <p class="text-gray-100">{{ endorser.data.bankAccountNumber || 'N/A' }}</p>
              </div>
            </div>
          </div>

          <div class="flex gap-3 pt-4">
            <button
              type="button"
              @click="closeCreatePayoutModal"
              :disabled="creatingPayout"
              class="flex-1 px-4 py-2 bg-warm-gray-700 hover:bg-warm-gray-600 text-gray-300 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="creatingPayout || calculatedCommission <= 0"
              class="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="creatingPayout">Creating...</span>
              <span v-else>Create Payout</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Toast Container -->
    <div
      class="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      style="pointer-events: none"
    >
      <TransitionGroup
        name="toast"
        tag="div"
        class="flex flex-col gap-2"
      >
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="pointer-events-auto min-w-[300px] max-w-md rounded-lg shadow-lg border p-4 animate-slide-in"
          :class="{
            'bg-green-50 border-green-200 text-green-800': toast.type === 'success',
            'bg-red-50 border-red-200 text-red-800': toast.type === 'error'
          }"
        >
          <div class="flex items-start justify-between gap-2">
            <p class="text-sm font-medium flex-1">{{ toast.message }}</p>
            <button
              @click="removeToast(toast.id)"
              class="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { db, auth } from '@/firebase/config.js'
import { collection, query, where, orderBy, onSnapshot, doc, getDoc, updateDoc, deleteDoc, Timestamp, addDoc, getDocs, writeBatch } from 'firebase/firestore'

const route = useRoute()

// Reactive state
const endorser = ref(null)
const loading = ref(true)
const error = ref(null)

const coupons = ref([])
const couponsLoading = ref(true)
const couponsError = ref(null)
const allUsedCouponsData = ref([])

// Separate state for used coupons (fetched by month)
const usedCouponsData = ref([])
const usedCouponsLoading = ref(false)

// Month browser state
const now = new Date()
const selectedMonth = ref(now.getMonth())
const selectedYear = ref(now.getFullYear())

// Coupon action state
const deactivatingCouponId = ref(null)
const activatingCouponId = ref(null)
const revokingCouponId = ref(null)

// Toast notification state
const toasts = ref([])
const toastTimers = new Map() // Store timers for auto-dismiss

// Store the unsubscribe functions for cleanup
let unsubscribeCoupons = null
let unsubscribeUsedCoupons = null
let unsubscribeAllUsedCoupons = null
let unsubscribePayouts = null

// Create Coupon State
const creatingCoupon = ref(false)

// Payouts state
const payouts = ref([])
const payoutsLoading = ref(true)
const payoutsError = ref(null)
const selectedPayout = ref(null)
const payoutToMarkAsPaid = ref(null)
const updatingPayoutId = ref(null)
const markAsPaidForm = ref({
  senderBankName: '',
  senderReferenceCode: ''
})

// Create Payout state
const showCreatePayoutModal = ref(false)
const creatingPayout = ref(false)
const createPayoutForm = ref({
  senderBankName: '',
  senderReferenceCode: '',
  notes: ''
})
const calculatedCommission = ref(0)
const unpaidCouponCount = ref(0)
const calculatingUnpaidCommission = ref(false)
const createPayoutError = ref(null)

// Endorser Edit State
const isEditingEndorser = ref(false)
const updatingEndorser = ref(false)
const endorserFormData = ref({
  firstName: '',
  lastName: '',
  email: '',
  personalEmail: '',
  commissionAmount: 0,
  discountAmount: 0,
  maxCoupons: 0,
  payoutThreshold: 0,
  bankName: '',
  bankAccountNumber: ''
})

/**
 * Get start and end dates for the selected month
 */
const getMonthDateRange = () => {
  const startDate = new Date(selectedYear.value, selectedMonth.value, 1)
  startDate.setHours(0, 0, 0, 0)
  
  const endDate = new Date(selectedYear.value, selectedMonth.value + 1, 0)
  endDate.setHours(23, 59, 59, 999)
  
  return {
    startDate: Timestamp.fromDate(startDate),
    endDate: Timestamp.fromDate(endDate)
  }
}

/**
 * Used coupons (fetched from database by month)
 */
const usedCoupons = computed(() => {
  return usedCouponsData.value
})

/**
 * Used coupons by selected month (already filtered by query)
 */
const usedCouponsByMonth = computed(() => {
  return usedCoupons.value
})

/**
 * Calculate total commission from used coupons in selected month
 */
const totalUsedCommission = computed(() => {
  return usedCouponsByMonth.value.reduce((sum, coupon) => {
    return sum + (coupon.data.commissionAmount || 0)
  }, 0)
})

const runningCommission = computed(() => {
  return allUsedCouponsData.value
    .filter(coupon => !coupon.data.isPaid)
    .reduce((sum, coupon) => sum + (coupon.data.commissionAmount || 0), 0)
})

/**
 * Filter unused coupons (status !== 'used')
 */
const unusedCoupons = computed(() => {
  return coupons.value.filter(coupon => !['used', 'revoked'].includes(coupon.data.status))
})

/**
 * Format currency amount
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

/**
 * Format date from Firestore timestamp
 */
const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A'

  let date
  if (timestamp?.toDate) {
    // Firestore Timestamp
    date = timestamp.toDate()
  } else if (timestamp instanceof Date) {
    date = timestamp
  } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    date = new Date(timestamp)
  } else {
    return 'N/A'
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

/**
 * Get UI status for a coupon
 * Maps actual status to display status, considering expiry date
 */
const getUIStatus = (coupon) => {
  const status = coupon.data.status
  const expiryDate = coupon.data.expiryDate
  
  // If coupon is revoked, always show as 'revoked'
  if (status === 'revoked') {
    return 'revoked'
  }

  // If coupon is used, always show as 'used'
  if (status === 'used') {
    // if coupon is paid, show as 'paid'
    if (coupon.data.isPaid) {
      return 'paid'
    } else {
      return 'used'
    }
  }
  
  // Check if coupon is expired (compare today with expiry date)
  if (expiryDate) {
    let expiry
    if (expiryDate?.toDate) {
      // Firestore Timestamp
      expiry = expiryDate.toDate()
    } else if (expiryDate instanceof Date) {
      expiry = expiryDate
    } else if (typeof expiryDate === 'string' || typeof expiryDate === 'number') {
      expiry = new Date(expiryDate)
    }
    
    if (expiry && expiry < new Date()) {
      return 'expired'
    }
  }
  
  // Map active/assigned to 'ready'
  if (status === 'active' || status === 'assigned') {
    return 'ready'
  }
  
  // Map deactivated to 'deactivated'
  if (status === 'deactivated') {
    return 'deactivated'
  }
  
  // Fallback: return the original status
  return status
}

/**
 * Get CSS classes for coupon UI status badge
 */
const getStatusClass = (uiStatus) => {
  const statusClasses = {
    ready: 'bg-green-900 text-green-200',
    used: 'bg-purple-900 text-purple-200',
    expired: 'bg-red-900 text-red-200',
    deactivated: 'bg-gray-700 text-gray-300',
    revoked: 'bg-red-900 text-red-200'
  }
  return statusClasses[uiStatus] || 'bg-gray-700 text-gray-300'
}

/**
 * Format month and year for display (e.g., "November 2025")
 */
const formatMonthYear = (month, year) => {
  const date = new Date(year, month, 1)
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

/**
 * Navigate to previous month
 */
const previousMonth = () => {
  if (selectedMonth.value === 0) {
    selectedMonth.value = 11
    selectedYear.value--
  } else {
    selectedMonth.value--
  }
  // Refresh used coupons for the new month
  setupUsedCouponsListener()
}

/**
 * Navigate to next month
 */
const nextMonth = () => {
  if (selectedMonth.value === 11) {
    selectedMonth.value = 0
    selectedYear.value++
  } else {
    selectedMonth.value++
  }
  // Refresh used coupons for the new month
  setupUsedCouponsListener()
}

/**
 * Show a toast notification
 * @param {string} message - Toast message
 * @param {string} type - Toast type: 'success' or 'error'
 * @param {number} duration - Duration in milliseconds (default: 3000)
 */
const showToast = (message, type = 'success', duration = 3000) => {
  const id = Date.now() + Math.random()
  const toast = { id, message, type }
  toasts.value.push(toast)

  // Auto-dismiss after duration
  const timer = setTimeout(() => {
    removeToast(id)
  }, duration)

  toastTimers.set(id, timer)
}

/**
 * Remove a toast notification
 * @param {number} id - Toast ID
 */
const removeToast = (id) => {
  // Clear timer if it exists
  const timer = toastTimers.get(id)
  if (timer) {
    clearTimeout(timer)
    toastTimers.delete(id)
  }

  // Remove toast from array
  const index = toasts.value.findIndex(t => t.id === id)
  if (index > -1) {
    toasts.value.splice(index, 1)
  }
}

/**
 * Deactivate a coupon
 * @param {string} couponId - Coupon document ID
 * @param {string} couponCode - Coupon code for display
 */
const deactivateCoupon = async (couponId, couponCode) => {
  if (deactivatingCouponId.value === couponId) return

  deactivatingCouponId.value = couponId

  try {
    const couponRef = doc(db, 'coupons', couponId)
    await updateDoc(couponRef, {
      status: 'deactivated'
    })

    showToast(`Coupon "${couponCode}" has been deactivated`, 'success')
  } catch (err) {
    console.error('Error deactivating coupon:', err)
    showToast(`Error deactivating coupon: ${err.message}`, 'error')
  } finally {
    deactivatingCouponId.value = null
  }
}

/**
 * Activate a coupon (reactivate from deactivated status)
 * Sets status to 'assigned' since coupons are assigned to endorsers
 * @param {string} couponId - Coupon document ID
 * @param {string} couponCode - Coupon code for display
 */
const activateCoupon = async (couponId, couponCode) => {
  if (activatingCouponId.value === couponId) return

  activatingCouponId.value = couponId

  try {
    const couponRef = doc(db, 'coupons', couponId)
    await updateDoc(couponRef, {
      status: 'assigned'
    })

    showToast(`Coupon "${couponCode}" has been activated`, 'success')
  } catch (err) {
    console.error('Error activating coupon:', err)
    showToast(`Error activating coupon: ${err.message}`, 'error')
  } finally {
    activatingCouponId.value = null
  }
}

/**
 * Revoke a coupon and zero out commission (invalid coupon)
 * @param {string} couponId - Coupon document ID
 * @param {string} couponCode - Coupon code for display
 */
const revokeCoupon = async (couponId, couponCode) => {
  if (revokingCouponId.value === couponId) return

  // Confirm revocation
  if (!confirm(`Are you sure you want to revoke coupon "${couponCode}"? This will mark it as invalid and zero out commission.`)) {
    return
  }

  revokingCouponId.value = couponId

  try {
    const couponRef = doc(db, 'coupons', couponId)
    await updateDoc(couponRef, {
      status: 'revoked',
      commissionAmount: 0,
      revokedAt: Timestamp.now()
    })

    showToast(`Coupon "${couponCode}" has been revoked`, 'success')
  } catch (err) {
    console.error('Error revoking coupon:', err)
    showToast(`Error revoking coupon: ${err.message}`, 'error')
  } finally {
    revokingCouponId.value = null
  }
}

/**
 * Fetch endorser data from Firestore
 */
const fetchEndorser = async () => {
  loading.value = true
  error.value = null
  
  try {
    const endorserId = route.params.id
    if (!endorserId) {
      error.value = 'Endorser ID is required'
      loading.value = false
      return
    }

    const endorserRef = doc(db, 'endorsers', endorserId)
    const endorserSnap = await getDoc(endorserRef)
    
    if (!endorserSnap.exists()) {
      error.value = 'Endorser not found'
      loading.value = false
      return
    }
    
    endorser.value = {
      id: endorserSnap.id,
      data: endorserSnap.data()
    }
    
    // Initialize form data
    initializeEndorserFormData()
    
    loading.value = false
    
    // Once endorser is loaded, set up coupons listeners
    setupCouponsListener()
    setupUsedCouponsListener()
    setupAllUsedCouponsListener()
    setupPayoutsListener()
  } catch (err) {
    console.error('Error fetching endorser:', err)
    error.value = `Error fetching endorser: ${err.message || 'Unknown error'}`
    loading.value = false
  }
}

/**
 * Set up real-time listener for unused coupons assigned to this endorser
 */
const setupCouponsListener = () => {
  if (!endorser.value || !endorser.value.data.email) {
    couponsLoading.value = false
    couponsError.value = 'Endorser email not found'
    coupons.value = []
    return
  }

  const email = endorser.value.data.email

  // Clean up any existing listener before setting up a new one
  if (unsubscribeCoupons) {
    unsubscribeCoupons()
    unsubscribeCoupons = null
  }

  try {
    couponsLoading.value = true
    couponsError.value = null

    const couponsRef = collection(db, 'coupons')
    // Query all coupons where assignedToEmail matches this endorser's email
    // We'll filter unused client-side since Firestore doesn't support != with multiple orderBy
    const q = query(
      couponsRef,
      where('assignedToEmail', '==', email),
      orderBy('createdAt', 'desc')
    )

    // Set up real-time listener
    unsubscribeCoupons = onSnapshot(
      q,
      (snapshot) => {
        // Filter out used coupons client-side
        coupons.value = snapshot.docs
          .map(doc => ({ id: doc.id, data: doc.data() }))
          .filter(coupon => coupon.data.status !== 'used')
        
        couponsLoading.value = false
        couponsError.value = null
      },
      (err) => {
        console.error('Error listening to coupons:', err)
        couponsError.value = `Error fetching coupons: ${err.message}`
        couponsLoading.value = false
      }
    )
  } catch (err) {
    couponsError.value = `Error setting up coupons listener: ${err.message}`
    console.error('Error setting up coupons listener:', err)
    couponsLoading.value = false
  }
}

/**
 * Set up real-time listener for used coupons assigned to this endorser for the selected month
 */
const setupUsedCouponsListener = () => {
  if (!endorser.value || !endorser.value.data.email) {
    usedCouponsLoading.value = false
    usedCouponsData.value = []
    return
  }

  const email = endorser.value.data.email
  const { startDate, endDate } = getMonthDateRange()

  // Clean up any existing listener before setting up a new one
  if (unsubscribeUsedCoupons) {
    unsubscribeUsedCoupons()
    unsubscribeUsedCoupons = null
  }

  try {
    usedCouponsLoading.value = true

    const couponsRef = collection(db, 'coupons')
    // Query used or revoked coupons where assignedToEmail matches and usedAt is within the selected month
    const q = query(
      couponsRef,
      where('assignedToEmail', '==', email),
      where('status', 'in', ['used', 'revoked']),
      where('usedAt', '>=', startDate),
      where('usedAt', '<=', endDate),
      orderBy('usedAt', 'desc')
    )

    // Set up real-time listener
    unsubscribeUsedCoupons = onSnapshot(
      q,
      (snapshot) => {
        usedCouponsData.value = snapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data()
        }))
        
        usedCouponsLoading.value = false
      },
      (err) => {
        console.error('Error listening to used coupons:', err)
        // If the error is due to missing index, try without date range
        if (err.message && err.message.includes('index')) {
          // Fallback: fetch all coupons and filter client-side
          const fallbackQ = query(
            couponsRef,
            where('assignedToEmail', '==', email),
            orderBy('usedAt', 'desc')
          )
          unsubscribeUsedCoupons = onSnapshot(
            fallbackQ,
            (snapshot) => {
              usedCouponsData.value = snapshot.docs
                .map(doc => ({ id: doc.id, data: doc.data() }))
                .filter(coupon => {
                  if (!coupon.data.usedAt) return false
                  if (!['used', 'revoked'].includes(coupon.data.status)) return false
                  const usedDate = coupon.data.usedAt?.toDate ? coupon.data.usedAt.toDate() : new Date(coupon.data.usedAt)
                  return usedDate >= startDate.toDate() && usedDate <= endDate.toDate()
                })
              usedCouponsLoading.value = false
            },
            (fallbackErr) => {
              console.error('Error listening to used coupons (fallback):', fallbackErr)
              usedCouponsLoading.value = false
            }
          )
        } else {
          usedCouponsLoading.value = false
        }
      }
    )
  } catch (err) {
    console.error('Error setting up used coupons listener:', err)
    usedCouponsLoading.value = false
  }
}

const setupAllUsedCouponsListener = () => {
  if (!endorser.value || !endorser.value.data.email) return

  const email = endorser.value.data.email

  if (unsubscribeAllUsedCoupons) {
    unsubscribeAllUsedCoupons()
    unsubscribeAllUsedCoupons = null
  }

  const q = query(
    collection(db, 'coupons'),
    where('assignedToEmail', '==', email),
    where('status', '==', 'used'),
    orderBy('usedAt', 'desc')
  )

  unsubscribeAllUsedCoupons = onSnapshot(q, (snapshot) => {
    allUsedCouponsData.value = snapshot.docs.map(doc => ({ id: doc.id, data: doc.data() }))
  }, (err) => {
    console.error('Error listening to all used coupons:', err)
  })
}

/**
 * Set up real-time listener for payouts for this endorser
 */
const setupPayoutsListener = () => {
  if (!endorser.value || !endorser.value.id) {
    payoutsLoading.value = false
    payoutsError.value = 'Endorser ID not found'
    payouts.value = []
    return
  }

  const endorserId = endorser.value.id

  // Clean up any existing listener before setting up a new one
  if (unsubscribePayouts) {
    unsubscribePayouts()
    unsubscribePayouts = null
  }

  try {
    payoutsLoading.value = true
    payoutsError.value = null

    const payoutsRef = collection(db, 'endorsers_payouts')
    const q = query(
      payoutsRef,
      where('endorserId', '==', endorserId),
      orderBy('requestedAt', 'desc')
    )

    // Set up real-time listener
    unsubscribePayouts = onSnapshot(
      q,
      (snapshot) => {
        payouts.value = snapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data()
        }))
        
        payoutsLoading.value = false
        payoutsError.value = null
      },
      (err) => {
        console.error('Error listening to payouts:', err)
        payoutsError.value = `Error fetching payouts: ${err.message}`
        payoutsLoading.value = false
      }
    )
  } catch (err) {
    payoutsError.value = `Error setting up payouts listener: ${err.message}`
    console.error('Error setting up payouts listener:', err)
    payoutsLoading.value = false
  }
}

/**
 * Get CSS classes for payout status badge
 */
const getPayoutStatusClass = (status) => {
  const statusClasses = {
    requested: 'bg-yellow-900 text-yellow-200',
    approved: 'bg-blue-900 text-blue-200',
    sent: 'bg-green-900 text-green-200',
    paid: 'bg-green-900 text-green-200',
    completed: 'bg-purple-900 text-purple-200',
    revoked: 'bg-red-900 text-red-200'
  }
  return statusClasses[status] || 'bg-gray-700 text-gray-300'
}

/**
 * Get display label for payout status
 */
const getPayoutStatusLabel = (status) => {
  const statusLabels = {
    requested: 'REQUESTED',
    approved: 'APPROVED',
    sent: 'PAID',
    paid: 'PAID',
    completed: 'COMPLETED',
    revoked: 'REVOKED'
  }
  return statusLabels[status] || status.toUpperCase()
}

/**
 * View payout details
 */
const viewPayout = (payout) => {
  selectedPayout.value = payout
}

/**
 * Close view payout modal
 */
const closeViewPayoutModal = () => {
  selectedPayout.value = null
}

/**
 * Open mark as paid modal
 */
const openMarkAsPaidModal = (payout) => {
  // Prevent opening modal for revoked payouts
  if (payout.data.status === 'revoked') {
    showToast('Cannot perform actions on a revoked payout', 'error')
    return
  }
  
  payoutToMarkAsPaid.value = payout
  markAsPaidForm.value = {
    senderBankName: payout.data.senderBankName || '',
    senderReferenceCode: payout.data.senderReferenceCode || ''
  }
}

/**
 * Close mark as paid modal
 */
const closeMarkAsPaidModal = () => {
  payoutToMarkAsPaid.value = null
  markAsPaidForm.value = {
    senderBankName: '',
    senderReferenceCode: ''
  }
}

/**
 * Handle mark as paid form submission
 */
const handleMarkAsPaid = async () => {
  if (updatingPayoutId.value || !payoutToMarkAsPaid.value) return

  const user = auth.currentUser
  if (!user) {
    showToast('User not authenticated', 'error')
    return
  }

  // Validate form
  if (!markAsPaidForm.value.senderBankName || markAsPaidForm.value.senderBankName.trim() === '') {
    showToast('Sender bank name is required', 'error')
    return
  }

  if (!markAsPaidForm.value.senderReferenceCode || markAsPaidForm.value.senderReferenceCode.trim() === '') {
    showToast('Reference code is required', 'error')
    return
  }

  updatingPayoutId.value = payoutToMarkAsPaid.value.id

  try {
    const payoutRef = doc(db, 'endorsers_payouts', payoutToMarkAsPaid.value.id)
    const currentStatus = payoutToMarkAsPaid.value.data.status
    
    // Prevent marking revoked payouts as paid
    if (currentStatus === 'revoked') {
      showToast('Cannot mark a revoked payout as paid', 'error')
      updatingPayoutId.value = null
      return
    }
    
    const updates = {
      status: 'paid',
      senderBankName: markAsPaidForm.value.senderBankName.trim(),
      senderReferenceCode: markAsPaidForm.value.senderReferenceCode.trim(),
      paidAt: Timestamp.now()
    }

    // Only set approval fields if status is "requested" (not already approved)
    if (currentStatus === 'requested') {
      updates.approvedAt = Timestamp.now()
      updates.approvedByEmail = user.email
      updates.approvedById = user.uid
    }

    await updateDoc(payoutRef, updates)

    showToast('Payout marked as paid successfully!', 'success')
    closeMarkAsPaidModal()
  } catch (err) {
    console.error('Error marking payout as paid:', err)
    showToast(`Error marking payout as paid: ${err.message}`, 'error')
  } finally {
    updatingPayoutId.value = null
  }
}

/**
 * Mark payout as complete
 */
const markPayoutAsComplete = async (payoutId) => {
  if (updatingPayoutId.value === payoutId) return

  // Confirm action
  if (!confirm('Are you sure you want to mark this payout as complete?')) {
    return
  }

  updatingPayoutId.value = payoutId

  try {
    const payoutRef = doc(db, 'endorsers_payouts', payoutId)
    
    // Get payout data to access includedCouponIds
    const payoutDoc = await getDoc(payoutRef)
    const payoutData = payoutDoc.data()
    
    // Update payout status to completed
    await updateDoc(payoutRef, {
      status: 'completed',
      completedAt: Timestamp.now()
    })

    // Mark all coupons in this payout as paid
    if (payoutData.includedCouponIds && payoutData.includedCouponIds.length > 0) {
      const batch = writeBatch(db)
      
      payoutData.includedCouponIds.forEach(couponId => {
        const couponRef = doc(db, 'coupons', couponId)
        batch.update(couponRef, { isPaid: true })
      })
      
      await batch.commit()
    }

    showToast('Payout marked as complete successfully!', 'success')
  } catch (err) {
    console.error('Error marking payout as complete:', err)
    showToast(`Error marking payout as complete: ${err.message}`, 'error')
  } finally {
    updatingPayoutId.value = null
  }
}

/**
 * Revoke a requested payout
 */
const revokePayout = async (payoutId) => {
  if (updatingPayoutId.value === payoutId) return

  // Confirm action
  if (!confirm('Are you sure you want to revoke this payout? Once revoked, no further actions can be taken on it.')) {
    return
  }

  updatingPayoutId.value = payoutId

  try {
    const payoutRef = doc(db, 'endorsers_payouts', payoutId)
    
    // Verify payout is in "requested" status
    const payoutDoc = await getDoc(payoutRef)
    const payoutData = payoutDoc.data()
    
    if (payoutData.status !== 'requested') {
      showToast('Can only revoke payouts that are in "requested" status', 'error')
      updatingPayoutId.value = null
      return
    }
    
    // Update payout status to revoked
    await updateDoc(payoutRef, {
      status: 'revoked',
      revokedAt: Timestamp.now()
    })

    showToast('Payout revoked successfully!', 'success')
  } catch (err) {
    console.error('Error revoking payout:', err)
    showToast(`Error revoking payout: ${err.message}`, 'error')
  } finally {
    updatingPayoutId.value = null
  }
}

/**
 * Delete a revoked payout
 */
const deletePayout = async (payoutId) => {
  if (updatingPayoutId.value === payoutId) return

  // Confirm action
  if (!confirm('Are you sure you want to delete this payout? This action cannot be undone.')) {
    return
  }

  updatingPayoutId.value = payoutId

  try {
    const payoutRef = doc(db, 'endorsers_payouts', payoutId)
    
    // Verify payout is in "revoked" status
    const payoutDoc = await getDoc(payoutRef)
    const payoutData = payoutDoc.data()
    
    if (payoutData.status !== 'revoked') {
      showToast('Can only delete payouts that are in "revoked" status', 'error')
      updatingPayoutId.value = null
      return
    }
    
    // Delete the payout document
    await deleteDoc(payoutRef)

    showToast('Payout deleted successfully!', 'success')
  } catch (err) {
    console.error('Error deleting payout:', err)
    showToast(`Error deleting payout: ${err.message}`, 'error')
  } finally {
    updatingPayoutId.value = null
  }
}

/**
 * Calculate unpaid commission from used coupons that haven't been included in completed payouts
 */
const calculateUnpaidCommission = async () => {
  if (!endorser.value || !endorser.value.data.email) {
    return { commission: 0, couponIds: [], count: 0 }
  }

  const endorserId = endorser.value.id
  const endorserEmail = endorser.value.data.email

  try {
    // Get all used coupons for this endorser
    const couponsRef = collection(db, 'coupons')
    const usedCouponsQuery = query(
      couponsRef,
      where('assignedToEmail', '==', endorserEmail),
      where('status', '==', 'used')
    )
    const usedCouponsSnapshot = await getDocs(usedCouponsQuery)

    // Get all completed payouts for this endorser
    const payoutsRef = collection(db, 'endorsers_payouts')
    const completedPayoutsQuery = query(
      payoutsRef,
      where('endorserId', '==', endorserId),
      where('status', '==', 'completed')
    )
    const completedPayoutsSnapshot = await getDocs(completedPayoutsQuery)

    // Collect all coupon IDs that have been paid out
    const paidCouponIds = new Set()
    completedPayoutsSnapshot.forEach(doc => {
      const payout = doc.data()
      if (payout.includedCouponIds && Array.isArray(payout.includedCouponIds)) {
        payout.includedCouponIds.forEach(couponId => paidCouponIds.add(couponId))
      }
    })

    // Calculate commission only from unpaid coupons
    const unpaidCouponIds = []
    let commissionAmount = 0
    usedCouponsSnapshot.forEach(doc => {
      const couponId = doc.id
      if (!paidCouponIds.has(couponId)) {
        const coupon = doc.data()
        unpaidCouponIds.push(couponId)
        commissionAmount += coupon.commissionAmount || 0
      }
    })

    return {
      commission: commissionAmount,
      couponIds: unpaidCouponIds,
      count: unpaidCouponIds.length
    }
  } catch (err) {
    console.error('Error calculating unpaid commission:', err)
    throw err
  }
}

/**
 * Open create payout modal and calculate unpaid commission
 */
const openCreatePayoutModal = async () => {
  if (!endorser.value) {
    showToast('Endorser information not loaded', 'error')
    return
  }

  showCreatePayoutModal.value = true
  createPayoutError.value = null
  calculatingUnpaidCommission.value = true

  try {
    const result = await calculateUnpaidCommission()
    calculatedCommission.value = result.commission
    unpaidCouponCount.value = result.count

    // Reset form
    createPayoutForm.value = {
      senderBankName: '',
      senderReferenceCode: '',
      notes: ''
    }
  } catch (err) {
    console.error('Error calculating unpaid commission:', err)
    createPayoutError.value = `Error calculating unpaid commission: ${err.message}`
  } finally {
    calculatingUnpaidCommission.value = false
  }
}

/**
 * Close create payout modal
 */
const closeCreatePayoutModal = () => {
  showCreatePayoutModal.value = false
  createPayoutForm.value = {
    senderBankName: '',
    senderReferenceCode: '',
    notes: ''
  }
  calculatedCommission.value = 0
  unpaidCouponCount.value = 0
  createPayoutError.value = null
}

/**
 * Handle create payout form submission
 */
const handleCreatePayout = async () => {
  if (creatingPayout.value || !endorser.value) return

  const user = auth.currentUser
  if (!user) {
    showToast('User not authenticated', 'error')
    return
  }

  // Validate form
  if (!createPayoutForm.value.senderBankName || createPayoutForm.value.senderBankName.trim() === '') {
    showToast('Sender bank name is required', 'error')
    return
  }

  if (!createPayoutForm.value.senderReferenceCode || createPayoutForm.value.senderReferenceCode.trim() === '') {
    showToast('Reference code is required', 'error')
    return
  }

  if (calculatedCommission.value <= 0) {
    showToast('No unpaid commission available', 'error')
    return
  }

  creatingPayout.value = true

  try {
    // Recalculate to get the latest unpaid coupon IDs
    const result = await calculateUnpaidCommission()
    
    if (result.commission <= 0 || result.couponIds.length === 0) {
      showToast('No unpaid commission available', 'error')
      creatingPayout.value = false
      return
    }

    const endorserId = endorser.value.id
    const endorserEmail = endorser.value.data.email
    const now = Timestamp.now()

    // Create payout data with status "paid"
    const payoutData = {
      endorserId,
      endorserEmail,
      requestedAt: now,
      requestedByEmail: user.email,
      requestedById: user.uid,
      commissionAmount: result.commission,
      includedCouponIds: result.couponIds,
      bonuses: [],
      totalAmount: result.commission,
      status: 'paid',
      recipientBankName: endorser.value.data.bankName || '',
      recipientBankAccountNumber: endorser.value.data.bankAccountNumber || '',
      senderBankName: createPayoutForm.value.senderBankName.trim(),
      senderReferenceCode: createPayoutForm.value.senderReferenceCode.trim(),
      approvedAt: now,
      approvedByEmail: user.email,
      approvedById: user.uid,
      paidAt: now,
      completedAt: null,
      notes: createPayoutForm.value.notes.trim() || ''
    }

    // Add payout to Firestore
    const payoutsRef = collection(db, 'endorsers_payouts')
    await addDoc(payoutsRef, payoutData)

    showToast('Payout created successfully!', 'success')
    closeCreatePayoutModal()
  } catch (err) {
    console.error('Error creating payout:', err)
    showToast(`Error creating payout: ${err.message}`, 'error')
  } finally {
    creatingPayout.value = false
  }
}

/**
 * Clean up the real-time listeners
 */
const cleanupCouponsListener = () => {
  if (unsubscribeCoupons) {
    unsubscribeCoupons()
    unsubscribeCoupons = null
  }
  if (unsubscribeUsedCoupons) {
    unsubscribeUsedCoupons()
    unsubscribeUsedCoupons = null
  }
  if (unsubscribeAllUsedCoupons) {
    unsubscribeAllUsedCoupons()
    unsubscribeAllUsedCoupons = null
  }
  if (unsubscribePayouts) {
    unsubscribePayouts()
    unsubscribePayouts = null
  }
}

/**
 * Generate a random 8-character alphanumeric code
 * @returns {string} Random code (e.g., "8F9881F7")
 */
const generateCouponCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let code = ''
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return code
}

/**
 * Create a new coupon for the endorser
 * Admin can bypass maxCoupons limit
 */
const createCoupon = async () => {
  if (creatingCoupon.value || !endorser.value) return

  const user = auth.currentUser
  if (!user) {
    showToast('User not authenticated', 'error')
    return
  }

  creatingCoupon.value = true

  try {
    // Get discount and commission amounts from endorser data
    const discountAmount = endorser.value.data.discountAmount
    const commissionAmount = endorser.value.data.commissionAmount

    // Validate that required values exist
    if (discountAmount == null || commissionAmount == null) {
      showToast('Missing required endorser data (discount or commission amount). Please contact support.', 'error')
      creatingCoupon.value = false
      return
    }

    // Generate coupon code
    const code = generateCouponCode()

    // Calculate expiry date (30 days from now)
    const now = new Date()
    const expiryDate = new Date(now)
    expiryDate.setDate(expiryDate.getDate() + 30)

    // Create coupon data
    const couponData = {
      code,
      assignedAt: Timestamp.now(),
      assignedToEmail: endorser.value.data.email,
      assignedToId: endorser.value.id,
      createdAt: Timestamp.now(),
      createdByEmail: user.email,
      createdById: user.uid,
      discountAmount,
      commissionAmount,
      expiryDate: Timestamp.fromDate(expiryDate),
      status: 'assigned',
      usedAt: null,
      usedByEmail: null,
      usedById: null,
      tags: []
    }

    // Add coupon to Firestore
    const couponsRef = collection(db, 'coupons')
    await addDoc(couponsRef, couponData)

    showToast(`Coupon "${code}" created successfully!`, 'success')
    // Real-time listener will automatically update the coupon list
  } catch (err) {
    console.error('Error creating coupon:', err)
    showToast(`Error creating coupon: ${err.message}`, 'error')
  } finally {
    creatingCoupon.value = false
  }
}

/**
 * Initialize endorser form data from current endorser data
 */
const initializeEndorserFormData = () => {
  if (!endorser.value) return
  
  endorserFormData.value = {
    firstName: endorser.value.data.firstName || '',
    lastName: endorser.value.data.lastName || '',
    email: endorser.value.data.email || '',
    personalEmail: endorser.value.data.personalEmail || '',
    commissionAmount: endorser.value.data.commissionAmount || 0,
    discountAmount: endorser.value.data.discountAmount || 0,
    maxCoupons: endorser.value.data.maxCoupons || 0,
    payoutThreshold: endorser.value.data.payoutThreshold || 0,
    bankName: endorser.value.data.bankName || '',
    bankAccountNumber: endorser.value.data.bankAccountNumber || ''
  }
}

/**
 * Toggle edit mode for endorser information
 */
const toggleEditMode = () => {
  if (isEditingEndorser.value) {
    // Cancel editing - reset form data
    initializeEndorserFormData()
  }
  isEditingEndorser.value = !isEditingEndorser.value
}

/**
 * Handle update endorser form submission
 */
const handleUpdateEndorser = async () => {
  if (updatingEndorser.value || !endorser.value) return

  const user = auth.currentUser
  if (!user) {
    showToast('User not authenticated', 'error')
    return
  }

  // Validate form data
  if (!endorserFormData.value.firstName || endorserFormData.value.firstName.trim() === '') {
    showToast('First name is required', 'error')
    return
  }

  if (!endorserFormData.value.lastName || endorserFormData.value.lastName.trim() === '') {
    showToast('Last name is required', 'error')
    return
  }

  if (!endorserFormData.value.personalEmail || !endorserFormData.value.personalEmail.includes('@')) {
    showToast('Valid personal email is required', 'error')
    return
  }

  if (!endorserFormData.value.commissionAmount || endorserFormData.value.commissionAmount <= 0) {
    showToast('Commission amount must be greater than 0', 'error')
    return
  }

  if (!endorserFormData.value.discountAmount || endorserFormData.value.discountAmount <= 0) {
    showToast('Discount amount must be greater than 0', 'error')
    return
  }

  if (!endorserFormData.value.maxCoupons || endorserFormData.value.maxCoupons <= 0) {
    showToast('Max coupons must be greater than 0', 'error')
    return
  }

  if (endorserFormData.value.payoutThreshold === null || endorserFormData.value.payoutThreshold === undefined || endorserFormData.value.payoutThreshold < 0) {
    showToast('Payout threshold must be a non-negative number', 'error')
    return
  }

  updatingEndorser.value = true

  try {
    const endorserRef = doc(db, 'endorsers', endorser.value.id)
    
    const updates = {
      firstName: endorserFormData.value.firstName.trim(),
      lastName: endorserFormData.value.lastName.trim(),
      personalEmail: endorserFormData.value.personalEmail.trim(),
      commissionAmount: endorserFormData.value.commissionAmount,
      discountAmount: endorserFormData.value.discountAmount,
      maxCoupons: endorserFormData.value.maxCoupons,
      payoutThreshold: endorserFormData.value.payoutThreshold,
      bankName: endorserFormData.value.bankName.trim() || '',
      bankAccountNumber: endorserFormData.value.bankAccountNumber.trim() || ''
    }

    await updateDoc(endorserRef, updates)

    // Refresh endorser data
    await fetchEndorser()

    showToast('Endorser information updated successfully!', 'success')
    isEditingEndorser.value = false
  } catch (err) {
    console.error('Error updating endorser:', err)
    showToast(`Error updating endorser: ${err.message}`, 'error')
  } finally {
    updatingEndorser.value = false
  }
}

// Watch for route changes
watch(
  () => route.params.id,
  () => {
    fetchEndorser()
  },
  { immediate: true }
)

// Clean up listener and toast timers when component unmounts
onUnmounted(() => {
  cleanupCouponsListener()
  // Clear all toast timers
  toastTimers.forEach(timer => clearTimeout(timer))
  toastTimers.clear()
})
</script>

<style scoped>
/* Toast animations */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-move {
  transition: transform 0.3s ease;
}

/* Slide in animation for toasts */
@keyframes slide-in {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slide-in {
  animation: slide-in 0.3s ease-out;
}
</style>
